import React, {useEffect, useState} from 'react'
import {ThemeProvider} from './initThemes'
import t from 'format-message'
import {Alert} from '@instructure/ui-alerts'
import {createRaven} from './raven'
import {initTranslations} from './initTranslations'
import {setUpDevHelpers} from './util/devHelpers'
import {shouldEnableMocking, enableMocking} from './util/enableMocking'
import {initializeStore} from './launchParams'
import {store} from './store'
import {Provider} from 'react-redux'
import {createRouter} from './router'
import {RouterProvider, RouterProviderProps} from 'react-router'
import {EntryPath} from './native/nativeLaunchTypes'
import {convertEntryPathToRoute} from './native/convertEntryPathToRoute'
import {QuizType} from './common/lib/quizType'
import {initializeNativeLaunchScripts} from './scriptInjection'

const raven = createRaven()

function captureError(error: Error | string | null = null) {
  if (error) {
    raven.captureException(error)
  } else {
    raven.captureMessage('Failed to launch')
  }
}

export interface AppProps {
  themeOverrides?: Record<string, string> | null
  launchType?: 'native' | 'lti'
  entryPath?: EntryPath
  basename?: string
  backendUrl?: string
  accessToken?: string
  assignmentId?: string
  courseWorkflowState?: string
  currentUser?: Record<string, unknown>
  highContrast?: boolean
  horizonAccount?: boolean
  horizonCourse?: boolean
  itemBanksScope?: Record<string, unknown>
  launchUrl?: string
  platform?: string
  returnToUrl?: string | null
  rldbLaunch?: string
  rcsHost?: string
  rcsJwt?: string
  contextType?: string
  canvasContextId?: string
  canvasLocalContextId?: number
  canvasToolId?: string
  userCanvasId?: string
  canvasHost?: string
  moduleLaunch?: string
  canvasAssignmentId?: string
  timeZone?: string
  restrictQuantitativeData?: boolean
  gradingScheme?: Array<{name: string; value: number}>
  launchToken?: string
  readOnly?: boolean
  decimalSeparator?: string
  thousandSeparator?: string
  aiQuizGeneration?: boolean
  assignmentAvailability?: {
    unlock_at: string | null
    lock_at: string | null
    due_at: string | null
  }
  locale?: string
  momentLocale?: string
  assignmentType?: QuizType
  anonymousParticipants?: boolean
  usageMetricsEnabled: boolean
}

export function App({launchType = 'lti', ...props}: AppProps) {
  const [initializationStatus, setInitializationStatus] = useState('idle')
  const [router, setRouter] = useState<RouterProviderProps['router'] | null>(null)

  useEffect(() => {
    if (initializationStatus === 'success' || initializationStatus === 'pending') {
      return
    }

    async function initApp() {
      setInitializationStatus('pending')

      if (shouldEnableMocking()) {
        // Enable MSW mocking if configured via environment variable
        await enableMocking()
      }

      // Initialize scripts for native launch (Learnosity, Pendo, etc.)
      if (launchType === 'native') {
        await initializeNativeLaunchScripts({
          locale: props.locale,
          momentLocale: props.momentLocale,
          launchParams: {
            access_token: props.accessToken,
            launch_token: props.launchToken,
            entry_path: props.entryPath,
            assignment_id: props.assignmentId,
            current_user: props.currentUser,
            item_banks_scope: props.itemBanksScope,
            course_workflow_state: props.courseWorkflowState,
            timezone: props.timeZone,
            high_contrast: props.highContrast,
            platform: props.platform,
            lang: props.locale,
            return_to: props.returnToUrl,
            rldb_launch: props.rldbLaunch,
            canvas_host: props.canvasHost,
            canvas_context_id: props.canvasContextId,
            canvas_local_context_id: props.canvasLocalContextId,
            canvas_tool_id: props.canvasToolId,
            canvas_assignment_id: props.canvasAssignmentId,
            user_canvas_id: props.userCanvasId,
            context_type: props.contextType,
            rcs_host: props.rcsHost,
            rcs_jwt: props.rcsJwt,
            usage_metrics_enabled: props.usageMetricsEnabled,
            ai_quiz_generation: props.aiQuizGeneration,
            decimal_separator: props.decimalSeparator,
            thousand_separator: props.thousandSeparator,
            grading_scheme: props.gradingScheme,
            restrict_quantitative_data: props.restrictQuantitativeData,
            participant_params: props.assignmentAvailability,
            read_only_mode: props.readOnly,
            moment_locale: props.momentLocale,
          },
          pendoConfig: {
            enabled: props.usageMetricsEnabled,
            pendoId: process.env.PENDO_ID,
            currentUser: props.currentUser
              ? {
                  uuid: (props.currentUser as {uuid?: string}).uuid || '',
                  roles: (props.currentUser as {roles?: string[]}).roles || [],
                  account_uuid: (props.currentUser as {account_uuid?: string}).account_uuid || '',
                }
              : undefined,
            locale: props.locale,
          },
          loadLearnosity: true,
        })
      }

      const translations = await initTranslations({
        locale: props.locale,
        moment_locale: props.momentLocale,
      })
      // If we do not have an access token, the session has expired & there isn't
      // much we can do about it.
      //
      // Send the user back to the last known return to url.
      if (!props.accessToken && props.returnToUrl) {
        window.location.replace(props.returnToUrl)
        return
      }

      // If we don't have an access token or a return url, something has gone
      // horribly wrong with the launch.
      //
      // Log an error and display an alert.
      if (!props.accessToken && !props.returnToUrl) {
        throw new Error('No access token or return URL provided')
      }

      setUpDevHelpers()
      initializeStore(store, {
        accessToken: props.accessToken,
        assignmentId: props.assignmentId,
        backendUrl: props.backendUrl,
        courseWorkflowState: props.courseWorkflowState,
        currentUser: props.currentUser,
        itemBanksScope: props.itemBanksScope,
        launchUrl: props.launchUrl,
        platform: props.platform,
        returnToUrl: props.returnToUrl,
        rldbLaunch: props.rldbLaunch,
        rcsHost: props.rcsHost,
        rcsJwt: props.rcsJwt,
        contextType: props.contextType,
        canvasContextId: props.canvasContextId,
        canvasLocalContextId: props.canvasLocalContextId,
        canvasToolId: props.canvasToolId,
        userCanvasId: props.userCanvasId,
        canvasHost: props.canvasHost,
        sdkTranslations: translations.quizSdk,
        moduleLaunch: props.moduleLaunch,
        canvasAssignmentId: props.canvasAssignmentId,
        timeZone: props.timeZone,
        restrictQuantitativeData: props.restrictQuantitativeData,
        gradingScheme: props.gradingScheme,
        launchToken: props.launchToken,
        readOnly: props.readOnly,
        decimalSeparator: props.decimalSeparator,
        thousandSeparator: props.thousandSeparator,
        aiQuizGeneration: props.aiQuizGeneration,
        assignmentAvailability: props.assignmentAvailability,
        locale: props.locale,
        assignmentType: props.assignmentType,
        anonymousParticipants: props.anonymousParticipants,
        horizonAccount: props.horizonAccount,
        horizonCourse: props.horizonCourse,
      })
    }

    initApp()
      .then(() => {
        setInitializationStatus('success')
      })
      .catch(error => {
        setInitializationStatus('error')
        captureError(error)
      })
  }, [initializationStatus, props])

  useEffect(() => {
    if (initializationStatus !== 'success' || router != null) {
      return
    }

    // Sanitize basename to remove duplicate path segments from the end
    // e.g., /courses/1/assignments/47/taking/40/taking/40 -> /courses/1/assignments/47/taking/40
    let sanitizedBasename = props.basename || ''

    if (sanitizedBasename && launchType === 'native') {
      const {workflow, resourceId} = props.entryPath || {
        workflow: 'build',
        resourceId: props.assignmentId,
      }
      const expectedPath = convertEntryPathToRoute({workflow, resourceId})

      // If basename ends with the expected path repeated, remove the duplicate
      if (sanitizedBasename.endsWith(expectedPath)) {
        sanitizedBasename = sanitizedBasename.slice(0, -expectedPath.length)
      }
    }

    setRouter(createRouter(sanitizedBasename))
  }, [
    initializationStatus,
    router,
    props.basename,
    launchType,
    props.entryPath,
    props.assignmentId,
  ])

  useEffect(() => {
    if (router == null || initializationStatus !== 'success' || launchType !== 'native') {
      return
    }

    const {workflow, resourceId} = props.entryPath || {
      workflow: 'build',
      resourceId: props.assignmentId,
    }

    const {pathname} = router.state.location
    const pathnameWithoutBasename = pathname.replace(props.basename || '', '')

    const reactRouterPath = convertEntryPathToRoute({workflow, resourceId})

    if (!pathnameWithoutBasename || pathnameWithoutBasename === '/') {
      router.navigate(reactRouterPath, {replace: true}).catch(captureError)
    }
  }, [
    props.basename,
    props.assignmentId,
    router,
    props.entryPath,
    launchType,
    initializationStatus,
  ])

  if (initializationStatus === 'pending') {
    return null
  }

  if (initializationStatus === 'error') {
    return (
      <Alert margin="large" variant="error">
        {t('Oops, something went wrong. We have logged the error and will investigate.')}
      </Alert>
    )
  }

  if (router == null) {
    return null
  }

  return (
    <ThemeProvider
      rldbLaunch={props.rldbLaunch}
      highContrast={props.highContrast}
      themeOverrides={props.themeOverrides}
    >
      <Provider store={store}>
        <RouterProvider router={router} />
      </Provider>
    </ThemeProvider>
  )
}
